/// <reference types="vite-plugin-svgr/client" />
import { useEffect, useMemo, useState } from 'react';
import { AppBar, Box, LinearProgress, Link as MuiLink, Stack, Toolbar } from '@mui/material';
import { Outlet, Link, Navigate } from 'react-router-dom';
import Logo from '~/assets/images/logo.svg?react';
import { LocationProvider } from '~/contexts/location.context';
import Navbar from './components/navbar';
import SearchBar from './components/search';
import UserDropdown from './components/user-dropdown';
import { SessionContextProvider, useSessionContext } from '~/contexts/session.context';
import { User } from '~/services/types';
import { useManageChat } from '~/modules/@shared/hooks/useManageChat';
import { array } from '~/modules/@shared/helpers/array';
import { RoleAuthenticationProvider } from '~/contexts/role-authentication';
import { TakePaymentModal } from '~/modules/payment/components/TakePayment/components/TakePaymentModal';
import { AddPaymentMethodModal } from '~/modules/payment/components/modals/AddPaymentMethodModal';
import { PendoPageTracker } from '~/modules/@shared/components';
import CreateInvoiceModal from '~/modules/@shared/components/CreateInvoiceModal/CreateInvoiceModal';
import useModalTemplateStore from '~/modules/@shared/components/ModalTemplate/hooks/useModalTemplateStore';

function MainLayout() {
  const { isAuth, currentUser, isLoading } = useSessionContext();

  const [closeChat, setCloseChat] = useState(false);
  const { tryChat } = useManageChat({ disabled: true, close: closeChat });
  const { tryChat: tryCLoseChat } = useManageChat({ disabled: true, close: true });

  const modal = useModalTemplateStore((state) => state.modal);

  useEffect(() => {
    const id = setTimeout(() => {
      setCloseChat(false);
      tryChat();
    }, 50);
    return () => {
      clearTimeout(id);
      tryCLoseChat();
    };
  }, [tryCLoseChat, tryChat]);

  const mainLocation = useMemo(() => {
    if (!isAuth) return null;

    const defaultLocation = array((currentUser as User).locations)
      .filter(Boolean)
      .find((y) => y.primary);
    if (!defaultLocation) return null;

    return defaultLocation;
  }, [currentUser]);

  if (isLoading) {
    return <LinearProgress />;
  }

  if (!isAuth || !mainLocation) {
    return <Navigate to="/login" />;
  } else if (currentUser?.email.indexOf('@') === -1) {
    return <Navigate to="/checkin" />;
  }

  return (
    <LocationProvider defaultLocation={mainLocation || location}>
      <Stack
        sx={{
          backgroundColor: '#f6f7fb',
          minHeight: '100dvh',
        }}>
        <AppBar
          component="nav"
          sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, backgroundColor: 'background.header' }}>
          <Toolbar sx={{ justifyContent: 'space-between' }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <MuiLink component={Link} to="/" sx={{ width: 116, display: 'inline-block' }}>
                <Logo />
              </MuiLink>
              <Navbar />
            </Stack>

            <Stack direction="row" spacing={1} sx={{ display: { xs: 'none', md: 'inherit' } }}>
              <SearchBar />
              {currentUser && <UserDropdown user={currentUser} />}
              <Navbar quickActions />
              {/* Placeholder for Pendo icon */}
              <Box sx={{ width: 40, height: 40 }}></Box>
            </Stack>
          </Toolbar>
        </AppBar>

        <Stack sx={{ flex: 1 }}>
          <Toolbar />
          <Outlet />
          <TakePaymentModal />
          <AddPaymentMethodModal />
          {modal === 'createInvoice' && <CreateInvoiceModal />}
        </Stack>

        {/* Pendo page tracking for authenticated users */}
        <PendoPageTracker />
      </Stack>
    </LocationProvider>
  );
}

function MainLayoutWrapper() {
  return (
    <SessionContextProvider>
      <RoleAuthenticationProvider>
        <MainLayout />
      </RoleAuthenticationProvider>
    </SessionContextProvider>
  );
}

export default MainLayoutWrapper;
